/**
 * MARKETPLACE CREATION DETAIL MODAL
 * 
 * Full-screen detail view for marketplace creations (press-ons, jewelry, etc.)
 * Features:
 * - Photo carousel with dots indicator
 * - Full creation details (description, techniques, difficulty, time)
 * - Press-on specific details (shapes, lengths, sizing options)
 * - Items used display (clickable with preview modal)
 * - Price and availability info
 * - Seller info with trust badges
 * - Navigation arrows for prev/next creations
 * - Contact Seller action
 * - Watch toggle (star icon)
 * 
 * UPDATED: Added watch functionality and clickable items used
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  X, ChevronLeft, ChevronRight, MessageCircle, Clock, Palette,
  User, Shield, Award, ExternalLink, Calendar,
  Sparkles, Zap, Loader2, Tag, Repeat, Star
} from 'lucide-react';
import { 
  doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { Creation } from '../../types/creationTypes';

interface MarketplaceCreation extends Creation {
  sellerId: string;
  sellerUsername?: string;
  openToTrades?: boolean;
}

interface SellerInfo {
  username: string | null;
  avatarUrl: string | null;
  memberSince: Date | null;
  vouchCount: number;
  isVerified: boolean;
}

// Interface for items used in creations
interface ItemUsed {
  itemId?: string;
  name: string;
  brand: string;
  photoUrl?: string;
  itemType?: string;
}

// Interface for the full item data when previewing
interface FullItemData {
  id: string;
  name: string;
  brand: string;
  itemType?: string;
  colorDescription?: string;
  photos?: { url: string; isDefault?: boolean }[];
  destashStatus?: string;
  destashPrice?: number;
  isOnDestash?: boolean;
  isOnTradelist?: boolean;
}

interface MarketplaceCreationDetailModalProps {
  creation: MarketplaceCreation;
  creations: MarketplaceCreation[]; // All creations for navigation
  currentIndex: number;
  currentUserId: string | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  isWatching?: boolean;
  onWatchChange?: (isWatching: boolean) => void;
}

const MarketplaceCreationDetailModal: React.FC<MarketplaceCreationDetailModalProps> = ({
  creation,
  creations,
  currentIndex,
  currentUserId,
  onClose,
  onNavigate,
  isWatching: initialIsWatching,
  onWatchChange
}) => {
  const navigate = useNavigate();
  
  // Photo carousel state
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Seller info state
  const [sellerInfo, setSellerInfo] = useState<SellerInfo>({
    username: null,
    avatarUrl: null,
    memberSince: null,
    vouchCount: 0,
    isVerified: false
  });
  const [loadingSeller, setLoadingSeller] = useState(true);
  
  // Contact state
  const [isContacting, setIsContacting] = useState(false);
  
  // Watching state (star icon)
  const [isWatching, setIsWatching] = useState(initialIsWatching ?? false);
  const [watchingLoading, setWatchingLoading] = useState(false);
  
  // Item preview modal state
  const [showItemPreview, setShowItemPreview] = useState(false);
  const [previewItem, setPreviewItem] = useState<FullItemData | null>(null);
  const [loadingPreviewItem, setLoadingPreviewItem] = useState(false);
  const [isItemWishlisted, setIsItemWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  
  // Login prompt state
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Get all photos
  const photos = creation.photos?.map(p => p.url) || [];

  // Fetch seller info including vouches
  useEffect(() => {
    const fetchSellerInfo = async () => {
      setLoadingSeller(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', creation.sellerId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Get vouch count
          const vouchesRef = collection(db, 'users', creation.sellerId, 'vouches');
          const vouchesSnapshot = await getDocs(vouchesRef);
          
          // Parse member since date
          let memberSince = null;
          const createdAt = userData.createdAt || userData.profile?.joinedDate;
          if (createdAt) {
            memberSince = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
          }
          
          setSellerInfo({
            username: userData.username || userData.profile?.username || null,
            avatarUrl: userData.avatarUrl || userData.profile?.avatarUrl || null,
            memberSince,
            vouchCount: vouchesSnapshot.size,
            isVerified: userData.emailVerified || false
          });
        }
      } catch (err) {
        console.error('Error fetching seller info:', err);
      } finally {
        setLoadingSeller(false);
      }
    };

    if (creation.sellerId) {
      fetchSellerInfo();
    }
  }, [creation.sellerId]);

  // Reset photo index when creation changes
  useEffect(() => {
    setCurrentPhotoIndex(0);
  }, [creation.id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        if (currentIndex > 0) {
          onNavigate(currentIndex - 1);
        }
      } else if (e.key === 'ArrowRight') {
        if (currentIndex < creations.length - 1) {
          onNavigate(currentIndex + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, creations.length, onClose, onNavigate]);

  // Check watching status (or sync with prop)
  useEffect(() => {
    // If prop is provided, use it
    if (initialIsWatching !== undefined) {
      setIsWatching(initialIsWatching);
      return;
    }
    
    // Otherwise, check from database
    const checkWatching = async () => {
      if (!currentUserId) return;
      
      try {
        const watchingRef = collection(db, 'users', currentUserId, 'watchingCreations');
        const snapshot = await getDocs(watchingRef);
        const isOnList = snapshot.docs.some(
          doc => doc.data().creationId === creation.id && doc.data().creatorId === creation.sellerId
        );
        setIsWatching(isOnList);
      } catch (err) {
        console.error('Error checking watching status:', err);
      }
    };

    checkWatching();
  }, [currentUserId, creation.id, creation.sellerId, initialIsWatching]);

  // Toggle watching
  const handleWatchingToggle = async () => {
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    setWatchingLoading(true);
    try {
      const watchingRef = collection(db, 'users', currentUserId, 'watchingCreations');
      
      if (isWatching) {
        const snapshot = await getDocs(watchingRef);
        const docToDelete = snapshot.docs.find(
          d => d.data().creationId === creation.id && d.data().creatorId === creation.sellerId
        );
        if (docToDelete) {
          await deleteDoc(doc(db, 'users', currentUserId, 'watchingCreations', docToDelete.id));
        }
        setIsWatching(false);
        onWatchChange?.(false);
      } else {
        const primaryPhoto = creation.photos?.find(p => p.isDefault) || creation.photos?.[0];
        await addDoc(watchingRef, {
          creationId: creation.id,
          creatorId: creation.sellerId,
          title: creation.title,
          photoUrl: primaryPhoto?.url || null,
          price: creation.price || null,
          marketplaceCategory: creation.marketplaceCategory || null,
          addedAt: serverTimestamp()
        });
        setIsWatching(true);
        onWatchChange?.(true);
      }
    } catch (err) {
      console.error('Error toggling watching:', err);
    } finally {
      setWatchingLoading(false);
    }
  };

  // Handle clicking on an item used
  const handleItemClick = async (item: ItemUsed) => {
    if (!item.itemId) return;
    
    setLoadingPreviewItem(true);
    setShowItemPreview(true);
    
    try {
      // Fetch the full item data from the creator's collection
      const itemRef = doc(db, 'users', creation.sellerId, 'items', item.itemId);
      const itemDoc = await getDoc(itemRef);
      
      if (itemDoc.exists()) {
        const data = itemDoc.data();
        setPreviewItem({
          id: itemDoc.id,
          name: data.name || item.name,
          brand: data.brand || item.brand,
          itemType: data.itemType,
          colorDescription: data.colorDescription,
          photos: data.photos,
          destashStatus: data.destashStatus,
          destashPrice: data.destashPrice,
          isOnDestash: data.isOnDestash,
          isOnTradelist: data.isOnTradelist
        });
        
        // Check if user has this on their wishlist
        if (currentUserId) {
          const wishlistRef = collection(db, 'users', currentUserId, 'marketplaceWishlist');
          const wishlistSnapshot = await getDocs(wishlistRef);
          const isOnWishlist = wishlistSnapshot.docs.some(
            d => d.data().itemId === item.itemId && d.data().sellerId === creation.sellerId
          );
          setIsItemWishlisted(isOnWishlist);
        }
      } else {
        // Item not found, show basic info
        setPreviewItem({
          id: item.itemId,
          name: item.name,
          brand: item.brand,
          itemType: item.itemType,
          photos: item.photoUrl ? [{ url: item.photoUrl, isDefault: true }] : undefined
        });
      }
    } catch (err) {
      console.error('Error fetching item:', err);
      // Show basic info on error
      setPreviewItem({
        id: item.itemId,
        name: item.name,
        brand: item.brand,
        itemType: item.itemType,
        photos: item.photoUrl ? [{ url: item.photoUrl, isDefault: true }] : undefined
      });
    } finally {
      setLoadingPreviewItem(false);
    }
  };

  // Toggle item wishlist from preview
  const handleItemWishlistToggle = async () => {
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }
    
    if (!previewItem) return;

    setWishlistLoading(true);
    try {
      const wishlistRef = collection(db, 'users', currentUserId, 'marketplaceWishlist');
      
      if (isItemWishlisted) {
        const snapshot = await getDocs(wishlistRef);
        const docToDelete = snapshot.docs.find(
          d => d.data().itemId === previewItem.id && d.data().sellerId === creation.sellerId
        );
        if (docToDelete) {
          await deleteDoc(doc(db, 'users', currentUserId, 'marketplaceWishlist', docToDelete.id));
        }
        setIsItemWishlisted(false);
      } else {
        const photo = previewItem.photos?.find(p => p.isDefault) || previewItem.photos?.[0];
        await addDoc(wishlistRef, {
          itemId: previewItem.id,
          sellerId: creation.sellerId,
          itemName: previewItem.name,
          itemBrand: previewItem.brand,
          itemType: previewItem.itemType,
          photoUrl: photo?.url || null,
          price: previewItem.destashPrice || null,
          addedAt: serverTimestamp()
        });
        setIsItemWishlisted(true);
      }
    } catch (err) {
      console.error('Error toggling item wishlist:', err);
    } finally {
      setWishlistLoading(false);
    }
  };

  // Close item preview
  const closeItemPreview = () => {
    setShowItemPreview(false);
    setPreviewItem(null);
    setIsItemWishlisted(false);
  };

  // Handle contact seller
  const handleContactSeller = async () => {
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    if (currentUserId === creation.sellerId) {
      alert("This is your own listing!");
      return;
    }

    setIsContacting(true);

    try {
      // Check if conversation already exists
      const conversationsRef = collection(db, 'conversations');
      const existingQuery = query(
        conversationsRef,
        where('participants', 'array-contains', currentUserId)
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      let conversationId: string | null = null;
      
      existingSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.participants.includes(creation.sellerId)) {
          conversationId = doc.id;
        }
      });

      // Prepare creation attachment
      const primaryPhoto = creation.photos?.find(p => p.isDefault) || creation.photos?.[0];
      const creationAttachment: Record<string, any> = {
        creationId: creation.id,
        ownerId: creation.sellerId,
        title: creation.title
      };
      if (creation.marketplaceCategory) creationAttachment.category = creation.marketplaceCategory;
      if (primaryPhoto?.url) creationAttachment.photoUrl = primaryPhoto.url;
      if (creation.price) creationAttachment.price = creation.price;

      if (conversationId) {
        // Add creation to pinned items if not already there
        const convRef = doc(db, 'conversations', conversationId);
        const convDoc = await getDoc(convRef);
        
        if (convDoc.exists()) {
          const convData = convDoc.data();
          const pinnedCreations = convData.pinnedCreations || [];
          
          if (!pinnedCreations.some((c: any) => c.creationId === creation.id)) {
            await updateDoc(convRef, {
              pinnedCreations: [...pinnedCreations, creationAttachment],
              updatedAt: serverTimestamp()
            });
          }
        }

        navigate(`/messages/${conversationId}`);
      } else {
        // Create new conversation
        const newConversation = {
          participants: [currentUserId, creation.sellerId],
          pinnedItems: [],
          pinnedCreations: [creationAttachment],
          lastMessage: {
            text: `Inquiry about: ${creation.title}`,
            senderId: currentUserId,
            timestamp: serverTimestamp()
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const docRef = await addDoc(conversationsRef, newConversation);

        // Add initial inquiry message
        const messagesRef = collection(db, 'conversations', docRef.id, 'messages');
        await addDoc(messagesRef, {
          senderId: currentUserId,
          type: 'inquiry',
          text: `Hi! I'm interested in your "${creation.title}"`,
          attachedCreations: [creationAttachment],
          timestamp: serverTimestamp(),
          readBy: [currentUserId]
        });

        navigate(`/messages/${docRef.id}`);
      }
    } catch (error) {
      console.error('Error contacting seller:', error);
      alert('Error starting conversation. Please try again.');
    } finally {
      setIsContacting(false);
    }
  };

  // Format member since
  const getMemberSince = (): string => {
    if (!sellerInfo.memberSince) return '';
    const options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
    return sellerInfo.memberSince.toLocaleDateString('en-US', options);
  };

  // Get category styling
  const getCategoryStyle = () => {
    switch (creation.marketplaceCategory) {
      case 'pressons':
        return { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-700', label: 'Press-Ons' };
      case 'jewelry':
        return { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-700', label: 'Jewelry' };
      case 'other':
        return { bg: 'bg-teal-500', light: 'bg-teal-100', text: 'text-teal-700', label: 'Other' };
      default:
        return { bg: 'bg-pink-500', light: 'bg-pink-100', text: 'text-pink-700', label: 'Creation' };
    }
  };

  // Get status styling
  const getStatusStyle = () => {
    switch (creation.marketplaceStatus) {
      case 'available':
        return { bg: 'bg-green-100', text: 'text-green-700', label: 'Available' };
      case 'pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' };
      case 'sold':
        return { bg: 'border-2 border-gray-300', text: 'text-light-secondary', label: 'Sold' };
      default:
        return { bg: 'bg-green-100', text: 'text-green-700', label: 'Available' };
    }
  };

  // Get difficulty styling
  const getDifficultyStyle = () => {
    switch (creation.difficulty as string) {
      case 'beginner':
        return { bg: 'bg-green-100', text: 'text-green-700' };
      case 'intermediate':
        return { bg: 'bg-blue-100', text: 'text-blue-700' };
      case 'advanced':
        return { bg: 'bg-orange-100', text: 'text-orange-700' };
      case 'expert':
        return { bg: 'bg-red-100', text: 'text-red-700' };
      default:
        return { bg: 'border-2 border-gray-300', text: 'text-light-secondary' };
    }
  };

  const categoryStyle = getCategoryStyle();
  const statusStyle = getStatusStyle();
  const isSold = creation.marketplaceStatus === 'sold';

  return (
    <>
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
      <div className="modal-light max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="modal-header-light">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 ${categoryStyle.bg} text-white text-sm font-bold rounded-full`}>
              {categoryStyle.label}
            </span>
            <span className={`px-3 py-1 ${statusStyle.bg} ${statusStyle.text} text-sm font-medium rounded-full`}>
              {statusStyle.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Watch button */}
            {!isSold && (
              <button
                onClick={handleWatchingToggle}
                disabled={watchingLoading}
                className={`p-2 rounded-full transition-all ${
                  isWatching
                    ? 'bg-amber-100 text-amber-500 hover:bg-amber-200'
                    : 'border-2 border-gray-300 text-light-muted hover:bg-gray-200 hover:text-amber-500'
                }`}
                title={isWatching ? 'Stop watching' : 'Wishlist/Watchlist'}
              >
                {watchingLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Star className={`w-5 h-5 ${isWatching ? 'fill-current' : ''}`} />
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-50 rounded-full transition-default"
            >
              <X className="icon-base text-light-muted" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="md:flex">
            {/* Photo Section */}
            <div className="md:w-1/2 p-6 border border-gray-200">
              {/* Main Photo */}
              <div className="relative aspect-square bg-white rounded-xl overflow-hidden shadow-lg mb-4">
                {photos.length > 0 ? (
                  <img
                    src={photos[currentPhotoIndex]}
                    alt={creation.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-fuchsia-200 to-purple-200 flex items-center justify-center">
                    <span className="text-8xl">💅</span>
                  </div>
                )}

                {/* Photo navigation */}
                {photos.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentPhotoIndex(prev => prev > 0 ? prev - 1 : photos.length - 1)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-default"
                    >
                      <ChevronLeft className="icon-md text-light-secondary" />
                    </button>
                    <button
                      onClick={() => setCurrentPhotoIndex(prev => prev < photos.length - 1 ? prev + 1 : 0)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-default"
                    >
                      <ChevronRight className="icon-md text-light-secondary" />
                    </button>
                  </>
                )}

                {/* Sold overlay */}
                {isSold && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="bg-white text-light-primary px-6 py-3 rounded-full font-bold text-xl">
                      SOLD
                    </span>
                  </div>
                )}
              </div>

              {/* Photo dots */}
              {photos.length > 1 && (
                <div className="flex justify-center gap-2">
                  {photos.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        idx === currentPhotoIndex
                          ? 'bg-purple-600 w-6'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Thumbnail strip */}
              {photos.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {photos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === currentPhotoIndex
                          ? 'border border-gray-300 shadow-md'
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={photo} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="md:w-1/2 p-6">
              {/* Title */}
              <h2 className="text-2xl font-bold text-light-primary mb-2 font-body">
                {creation.title}
              </h2>

              {/* Description */}
              {creation.description && (
                <p className="text-light-secondary mb-4 font-body">
                  {creation.description}
                </p>
              )}

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-2 mb-6">
                {creation.difficulty && (
                  <span className={`px-3 py-1 ${getDifficultyStyle().bg} ${getDifficultyStyle().text} rounded-full text-sm font-medium capitalize`}>
                    <Zap className="icon-xs inline mr-1" />
                    {creation.difficulty}
                  </span>
                )}
                {creation.timeToComplete && (
                  <span className="px-3 py-1 border-2 border-gray-300 text-light-secondary rounded-full text-sm font-medium">
                    <Clock className="icon-xs inline mr-1" />
                    {creation.timeToComplete}
                  </span>
                )}
                {creation.itemsUsed && creation.itemsUsed.length > 0 && (
                  <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                    <Palette className="icon-xs inline mr-1" />
                    {creation.itemsUsed.length} items used
                  </span>
                )}
                {creation.dateCreated && (
                  <span className="px-3 py-1 border-2 border-gray-300 text-light-secondary rounded-full text-sm font-medium">
                    <Calendar className="icon-xs inline mr-1" />
                    {new Date(creation.dateCreated).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Techniques */}
              {creation.techniques && creation.techniques.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-light-secondary mb-2 flex items-center gap-2">
                    <Sparkles className="icon-sm" />
                    Techniques Used
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {creation.techniques.map((technique, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-light-primary rounded-full text-sm"
                      >
                        {technique}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Items Used */}
              {creation.itemsUsed && creation.itemsUsed.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-light-secondary mb-2 flex items-center gap-2">
                    <Palette className="icon-sm" />
                    Items Used
                    <span className="text-xs font-normal text-light-muted">(tap to view)</span>
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {creation.itemsUsed.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleItemClick(item as ItemUsed)}
                        disabled={!item.itemId}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all text-left ${
                          item.itemId 
                            ? 'border border-gray-200 hover:bg-gray-50 hover:border-gray-300 border-transparent cursor-pointer' 
                            : 'border border-gray-200 cursor-default'
                        }`}
                      >
                        {item.photoUrl ? (
                          <img src={item.photoUrl} alt="" className="w-10 h-10 rounded object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                            <span className="text-sm">💅</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-light-primary truncate">{item.name}</p>
                          <p className="text-xs text-light-muted truncate">{item.brand}</p>
                        </div>
                        {item.itemId && (
                          <ExternalLink className="icon-sm text-light-muted" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Press-On Details */}
              {creation.marketplaceCategory === 'pressons' && creation.pressOnDetails && (
                <div className="mb-6 p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <h4 className="text-sm font-bold text-purple-700 mb-3">Press-On Options</h4>
                  
                  {creation.pressOnDetails.shapesAvailable && creation.pressOnDetails.shapesAvailable.length > 0 && (
                    <div className="mb-2">
                      <span className="text-sm text-light-secondary">Shapes: </span>
                      <span className="text-sm text-purple-700 font-medium">
                        {creation.pressOnDetails.shapesAvailable.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {creation.pressOnDetails.lengthsAvailable && creation.pressOnDetails.lengthsAvailable.length > 0 && (
                    <div className="mb-2">
                      <span className="text-sm text-light-secondary">Lengths: </span>
                      <span className="text-sm text-purple-700 font-medium">
                        {creation.pressOnDetails.lengthsAvailable.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2 mt-2">
                    {creation.pressOnDetails.customSizing && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        ✓ Custom Sizing
                      </span>
                    )}
                    {creation.pressOnDetails.sizingKitAvailable && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        ✓ Sizing Kit Available
                      </span>
                    )}
                  </div>
                  
                  {creation.pressOnDetails.guideUrl && (
                    <a
                      href={creation.pressOnDetails.guideUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 font-medium"
                    >
                      <ExternalLink className="icon-sm" />
                      View Sizing Guide
                    </a>
                  )}
                </div>
              )}

              {/* Pricing */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-100">
                <h4 className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                  <Tag className="icon-sm" />
                  Pricing
                </h4>
                
                <div className="flex flex-wrap items-center gap-3">
                  {creation.price ? (
                    <span className={`px-4 py-2 rounded-full text-lg font-bold ${
                      isSold ? 'bg-gray-200 text-light-muted line-through' : 'bg-green-500 text-white'
                    }`}>
                      ${creation.price.toFixed(2)}
                    </span>
                  ) : creation.contactForPricing ? (
                    <span className="px-4 py-2 border-2 border-gray-300 text-light-secondary rounded-full text-sm font-medium">
                      Contact for Pricing
                    </span>
                  ) : null}
                  
                  {creation.openToTrades && (
                    <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-1">
                      <Repeat className="icon-sm" />
                      Open to Trades
                    </span>
                  )}
                  
                  {creation.quantity && creation.quantity > 1 && (
                    <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                      {creation.quantity} available
                    </span>
                  )}
                </div>
              </div>

              {/* Seller Info */}
              <div className="border border-gray-200 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-bold text-light-secondary mb-3 flex items-center gap-2">
                  <User className="icon-sm" />
                  Creator
                </h4>
                
                {loadingSeller ? (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2" />
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-32" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <Link
                      to={sellerInfo.username ? `/profile/${sellerInfo.username}` : '#'}
                      className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                    >
                      {sellerInfo.avatarUrl ? (
                        <img
                          src={sellerInfo.avatarUrl}
                          alt={sellerInfo.username || ''}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full rainbow-gradient flex items-center justify-center text-white text-lg font-bold shadow">
                          {sellerInfo.username?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-light-primary font-body">
                          @{sellerInfo.username || 'Unknown'}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-light-muted">
                          {sellerInfo.memberSince && (
                            <span className="flex items-center gap-1">
                              <Calendar className="icon-xs" />
                              Member since {getMemberSince()}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                    
                    {/* Trust badges */}
                    <div className="flex flex-col items-end gap-1">
                      {sellerInfo.vouchCount > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          <Award className="icon-xs" />
                          {sellerInfo.vouchCount} vouch{sellerInfo.vouchCount !== 1 ? 'es' : ''}
                        </span>
                      )}
                      {sellerInfo.isVerified && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          <Shield className="icon-xs" />
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {sellerInfo.username && (
                  <Link
                    to={`/profile/${sellerInfo.username}`}
                    className="mt-3 flex items-center justify-center gap-1 text-sm text-light-secondary hover:text-light-primary font-medium"
                  >
                    View Full Profile
                    <ExternalLink className="icon-sm" />
                  </Link>
                )}
              </div>

              {/* Action Button */}
              {!isSold ? (
                <button
                  onClick={handleContactSeller}
                  disabled={isContacting || !currentUserId || creation.sellerId === currentUserId}
                  className={`w-full py-3 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                    !currentUserId || creation.sellerId === currentUserId
                      ? 'bg-gray-200 text-light-muted cursor-not-allowed'
                      : 'btn-primary shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isContacting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Opening Chat...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="icon-md" />
                      Contact Seller
                    </>
                  )}
                </button>
              ) : (
                <div className="text-center py-4 text-light-muted font-body">
                  This item has been sold
                </div>
              )}

              {!currentUserId && !isSold && (
                <p className="text-center text-sm text-light-muted mt-3 font-body">
                  Sign in to contact the seller
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border border-gray-300 border border-gray-200">
          <button
            onClick={() => onNavigate(currentIndex - 1)}
            disabled={currentIndex === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-default ${
              currentIndex === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-light-secondary hover:bg-gray-200'
            }`}
          >
            <ChevronLeft className="icon-md" />
            Previous
          </button>
          
          <span className="text-sm text-light-muted">
            {currentIndex + 1} of {creations.length}
          </span>
          
          <button
            onClick={() => onNavigate(currentIndex + 1)}
            disabled={currentIndex === creations.length - 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-default ${
              currentIndex === creations.length - 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-light-secondary hover:bg-gray-200'
            }`}
          >
            Next
            <ChevronRight className="icon-md" />
          </button>
        </div>
      </div>
    </div>

    {/* Item Preview Modal */}
    {showItemPreview && (
      <div 
        className="fixed inset-0 bg-black/60 z-[160] flex items-center justify-center p-4"
        onClick={closeItemPreview}
      >
        <div 
          className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {loadingPreviewItem ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-light-primary" />
            </div>
          ) : previewItem ? (
            <>
              {/* Item Photo */}
              <div className="aspect-square relative border-2 border-gray-300">
                {previewItem.photos && previewItem.photos.length > 0 ? (
                  <img
                    src={previewItem.photos.find(p => p.isDefault)?.url || previewItem.photos[0].url}
                    alt={previewItem.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-fuchsia-200 to-purple-200 flex items-center justify-center">
                    <span className="text-6xl">💅</span>
                  </div>
                )}
                
                {/* Close button */}
                <button
                  onClick={closeItemPreview}
                  className="absolute top-3 right-3 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-default"
                >
                  <X className="icon-md text-light-secondary" />
                </button>

                {/* Status badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {previewItem.itemType && (
                    <span className="px-2 py-1 bg-gray-700 text-white text-xs font-bold rounded-full shadow capitalize">
                      {previewItem.itemType}
                    </span>
                  )}
                  {previewItem.isOnDestash && previewItem.destashStatus === 'available' && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow">
                      For Sale
                    </span>
                  )}
                  {previewItem.isOnTradelist && (
                    <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-full shadow flex items-center gap-1">
                      <Repeat className="icon-xs" />
                      Trade
                    </span>
                  )}
                </div>
              </div>

              {/* Item Info */}
              <div className="p-4">
                <p className="text-xs text-light-primary font-bold uppercase tracking-wide mb-1 font-heading">
                  {previewItem.brand}
                </p>
                <h3 className="text-lg font-bold text-light-primary mb-2 font-body">
                  {previewItem.name}
                </h3>
                
                {previewItem.colorDescription && (
                  <p className="text-sm text-light-secondary mb-3">
                    {previewItem.colorDescription}
                  </p>
                )}

                {/* Price if on destash */}
                {previewItem.isOnDestash && previewItem.destashPrice && (
                  <div className="mb-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-bold">
                      <Tag className="icon-sm" />
                      ${previewItem.destashPrice}
                    </span>
                  </div>
                )}

                {/* Creator info */}
                <div className="flex items-center gap-2 text-sm text-light-muted mb-4">
                  <span>From</span>
                  {sellerInfo.avatarUrl ? (
                    <img src={sellerInfo.avatarUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500" />
                  )}
                  <span className="font-medium text-light-secondary">@{sellerInfo.username}</span>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2">
                  {/* Add to PublicWishlist/Watching */}
                  {previewItem.isOnDestash || previewItem.isOnTradelist ? (
                    <button
                      onClick={handleItemWishlistToggle}
                      disabled={wishlistLoading || creation.sellerId === currentUserId}
                      className={`flex-1 py-2.5 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                        isItemWishlisted
                          ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                          : creation.sellerId === currentUserId
                            ? 'border-2 border-gray-300 text-light-muted cursor-not-allowed'
                            : 'btn-primary'
                      }`}
                    >
                      {wishlistLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isItemWishlisted ? (
                        <>
                          <Star className="icon-sm fill-current" />
                          Watching
                        </>
                      ) : (
                        <>
                          <Star className="icon-sm" />
                          Watch Item
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="flex-1 py-2.5 px-4 border-2 border-gray-300 text-light-muted rounded-xl text-sm text-center font-body">
                      Not currently listed
                    </div>
                  )}
                </div>
                
                {creation.sellerId === currentUserId && (
                  <p className="text-xs text-light-muted text-center mt-2">
                    This is from your collection
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <p className="text-light-muted">Item not found</p>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Login Prompt Modal */}
    {showLoginPrompt && (
      <div 
        className="fixed inset-0 bg-black/50 z-[160] flex items-center justify-center p-4"
        onClick={() => setShowLoginPrompt(false)}
      >
        <div 
          className="bg-white rounded-xl p-6 max-w-sm w-full text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-5xl mb-4">🔐</div>
          <h3 className="text-xl font-bold text-light-primary mb-2 font-heading">
            Sign In Required
          </h3>
          <p className="text-light-secondary mb-4 font-body">
            You need to be signed in to watch creations and contact sellers.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLoginPrompt(false)}
              className="flex-1 px-4 py-2 border-2 border-gray-300 text-light-secondary rounded-lg hover:bg-gray-200 transition-default font-body"
            >
              Cancel
            </button>
            <Link
              to="/auth"
              className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-default text-center font-body"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default MarketplaceCreationDetailModal;
